export type AutofillConfidence = "strong" | "medium" | "low";

export const AUTOFILL_TRANSLATION_KEY = "settings.deals.workflow.aiAutofill";

// How long a renamed out-of-the-box field's new name must sit still before its
// prompt is generated, so a pause between words doesn't fire a request.
export const AUTOFILL_RENAME_GENERATE_DEBOUNCE_MS = 800;

/**
 * Who owns a workflow field's autofill prompt.
 *
 * `system` — a field Dreamer fills through a hardcoded connector, so there is no
 * admin-authored prompt to show or write; only the on/off switch applies.
 * `converting` — a connector-filled field whose name the admin has edited. It becomes an
 * ordinary field on save, so it needs a prompt of its own.
 * `own` — any other field, out-of-the-box or not, which carries its own prompt.
 *
 * One value rather than a pair of booleans so the two halves cannot be passed out of sync.
 */
export type AutofillPromptOwner = "system" | "converting" | "own";

export const autofillPromptOwnerFor = (
  hasAutofillConnector: boolean,
  isRenamed: boolean
): AutofillPromptOwner => {
  if (!hasAutofillConnector) return "own";
  return isRenamed ? "converting" : "system";
};

// Activity types an admin can pick to trigger autofill; automator also knows `attachment`
// (no classifier yet) and `no_path` (no source), which are never offered.
export const AUTOFILL_TRIGGER_ACTIVITY_TYPES = [
  "email",
  "online_meeting_transcript",
  "in_person_meeting",
  "call",
  "invite"
] as const;

export const AUTOFILL_NO_TRIGGER_PATH = "no_path";

export const AUTOFILL_TEST_PROGRESS_INTERVAL_MS = 10000;
export const AUTOFILL_TEST_PROGRESS_STEP = 10;
export const AUTOFILL_TEST_PROGRESS_CEILING = 90;

export const AUTOFILL_CARD_BACKGROUND =
  "radial-gradient(233.67% 235.44% at 76.69% 149.84%, rgba(112, 243, 176, 0.20) 0%, rgba(0, 85, 255, 0.20) 3.8%, rgba(4, 7, 11, 0.00) 52.11%), linear-gradient(0deg, rgba(255, 255, 255, 0.02) 0%, rgba(255, 255, 255, 0.02) 100%), #161E2C";

export interface BadgeStyle {
  background: string;
  textColor: string;
}

export const AUTOFILL_CONFIDENCE_BADGE: Record<AutofillConfidence, BadgeStyle> =
  {
    strong: {
      background: "bg-[rgba(var(--surface-chips-positive-rgb),0.18)]",
      textColor: "text-charts-positive"
    },
    medium: {
      background: "bg-[rgba(var(--surface-chips-warning-rgb),0.18)]",
      textColor: "text-warning"
    },
    low: {
      background: "bg-[rgba(var(--surface-chips-negative-rgb),0.18)]",
      textColor: "text-error"
    }
  };
